import * as React from 'react';
import { NumberInputField, TimeField } from '../../../components/Forms/fields';
import { AutocompleteTextField } from '../../../components/Forms/fields/AutocompleteTextField';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import Typography from '../../../components/Typography';
import { AlertsSettingsData, ProfessionalData } from '../../../models';
import { useEntities } from '../../../store/entities/hooks';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { SettingsItemContentProps } from '../SettingsItem';

interface ProfessionalRemindersSettingsProps extends SettingsItemContentProps {
    values: AlertsSettingsData;
    onChange: (settings: AlertsSettingsData) => void;
}

const ProfessionalRemindersSettings: React.FC<ProfessionalRemindersSettingsProps> = ({ values, onChange }) => {
    const t = useTranslations();
    const professionals = useEntities<ProfessionalData>('professionals');
    const professionalAlerts = values.professional ?? {};

    const professionalNameOptions = React.useMemo(
        () => professionals.map(p => p.name),
        [professionals]
    );

    const selectedProfessionalNames = React.useMemo(
        () => (professionalAlerts?.nextDaySummaryForProfessionals ?? [])
            .map(id => professionals.find(p => p.id === id)?.name)
            .filter((name): name is string => name !== undefined),
        [professionalAlerts?.nextDaySummaryForProfessionals, professionals]
    );

    const onChangeProfessional = React.useCallback((nextProfessional: AlertsSettingsData['professional']) => {
        onChange({
            ...values,
            professional: nextProfessional,
        });
    }, [onChange, values]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <Typography size="smaller">{t(TK.professionalReminders)}</Typography>

            <CheckInputField
                label={t(TK.alertWhenAppointmentIsCreated)}
                value={(professionalAlerts?.appointmentCreatedOnNextHours ?? null) !== null}
                onChange={enabled => onChangeProfessional({
                    ...professionalAlerts,
                    appointmentCreatedOnNextHours: enabled ? 48 : undefined,
                })}
            />
            {(professionalAlerts?.appointmentCreatedOnNextHours ?? null) !== null && (
                <NumberInputField
                    style={{ maxWidth: '15rem' }}
                    prepend={t(TK.inTheNext)}
                    append={t(TK.hours)}
                    value={professionalAlerts?.appointmentCreatedOnNextHours}
                    onChange={appointmentCreatedOnNextHours => onChangeProfessional({
                        ...professionalAlerts,
                        appointmentCreatedOnNextHours,
                    })}
                />
            )}

            <CheckInputField
                label={t(TK.alertWhenAppointmentIsUpdated)}
                value={(professionalAlerts?.appointmentChangedOnNextHours ?? null) !== null}
                onChange={enabled => onChangeProfessional({
                    ...professionalAlerts,
                    appointmentChangedOnNextHours: enabled ? 48 : undefined,
                })}
            />
            {(professionalAlerts?.appointmentChangedOnNextHours ?? null) !== null && (
                <NumberInputField
                    style={{ maxWidth: '15rem' }}
                    prepend={t(TK.inTheNext)}
                    append={t(TK.hours)}
                    value={professionalAlerts?.appointmentChangedOnNextHours}
                    onChange={appointmentChangedOnNextHours => onChangeProfessional({
                        ...professionalAlerts,
                        appointmentChangedOnNextHours,
                    })}
                />
            )}

            <CheckInputField
                label={t(TK.alertWhenAppointmentIsCancelled)}
                value={(professionalAlerts?.appointmentCancelledOnNextHours ?? null) !== null}
                onChange={enabled => onChangeProfessional({
                    ...professionalAlerts,
                    appointmentCancelledOnNextHours: enabled ? 48 : undefined,
                })}
            />
            {(professionalAlerts?.appointmentCancelledOnNextHours ?? null) !== null && (
                <NumberInputField
                    style={{ maxWidth: '15rem' }}
                    prepend={t(TK.inTheNext)}
                    append={t(TK.hours)}
                    value={professionalAlerts?.appointmentCancelledOnNextHours}
                    onChange={appointmentCancelledOnNextHours => onChangeProfessional({
                        ...professionalAlerts,
                        appointmentCancelledOnNextHours,
                    })}
                />
            )}

            <CheckInputField
                label={t(TK.nextDaySummary)}
                value={(professionalAlerts?.nextDaySummarySendingTime ?? null) !== null}
                onChange={enabled => onChangeProfessional({
                    ...professionalAlerts,
                    nextDaySummarySendingTime: enabled ? '18:00' : undefined,
                    nextDaySummaryForProfessionals: enabled ? professionalAlerts.nextDaySummaryForProfessionals : undefined,
                })}
            />
            {(professionalAlerts?.nextDaySummarySendingTime ?? null) !== null && (
                <div style={{ display: 'flex', gap: 'var(--spacer-S)', alignItems: 'flex-start' }}>
                    <TimeField
                        style={{ maxWidth: '10rem', flexShrink: 0 }}
                        label={t(TK.sendingTime)}
                        value={professionalAlerts?.nextDaySummarySendingTime}
                        onChange={nextDaySummarySendingTime => onChangeProfessional({
                            ...professionalAlerts,
                            nextDaySummarySendingTime,
                        })}
                    />
                    <AutocompleteTextField
                        style={{ flex: 1 }}
                        label={t(TK.nextDaySummaryForProfessionals)}
                        placeholder={selectedProfessionalNames.length === 0
                            ? t(TK.willBeSentToAllProfessionals)
                            : undefined}
                        multiSelect
                        options={professionalNameOptions}
                        values={selectedProfessionalNames}
                        onValuesChanged={names => onChangeProfessional({
                            ...professionalAlerts,
                            nextDaySummaryForProfessionals: names
                                .map(name => professionals.find(p => p.name === name)?.id)
                                .filter((id): id is string => id !== undefined),
                        })}
                    />
                </div>
            )}
        </div>
    );
};

export default ProfessionalRemindersSettings;
